'use client';

import React, { useState } from 'react';
import { CITIES } from '../../muslim-resource-guide/components/cityData';
import CaretakerSubmissionForm from '../components/CaretakerSubmissionForm';

interface EmbedConfig {
  city: string;
  ward: string | null;
  theme: 'dark' | 'light' | 'forest';
  compact: boolean;
  tab: 'resources' | 'disparities' | 'context';
  width: string;
  height: string;
}

const DEFAULT_CONFIG: EmbedConfig = {
  city: 'atlanta',
  ward: null,
  theme: 'dark',
  compact: false,
  tab: 'resources',
  width: '100%',
  height: '600px',
};

function buildEmbedUrl(config: EmbedConfig): string {
  const base = 'https://haalproject.org/mrg-embed';
  const params = new URLSearchParams();
  params.set('city', config.city);
  if (config.ward) params.set('ward', config.ward);
  if (config.theme !== 'dark') params.set('theme', config.theme);
  if (config.compact) params.set('compact', 'true');
  if (config.tab !== 'resources') params.set('tab', config.tab);
  return `${base}?${params.toString()}`;
}

function buildIframeCode(config: EmbedConfig): string {
  const url = buildEmbedUrl(config);
  return `<iframe
  src="${url}"
  width="${config.width}"
  height="${config.height}"
  frameborder="0"
  style="border:none;border-radius:8px;"
  title="Muslim Resource Guide — ${config.city}${config.ward ? ` · ${config.ward}` : ''}"
  loading="lazy"
></iframe>`;
}

function buildScriptCode(config: EmbedConfig): string {
  const url = buildEmbedUrl(config);
  return `<div id="haal-mrg-widget" data-city="${config.city}"${config.ward ? ` data-ward="${config.ward}"` : ''} data-theme="${config.theme}" style="width:${config.width};height:${config.height};"></div>
<script>
  (function() {
    var el = document.getElementById('haal-mrg-widget');
    var iframe = document.createElement('iframe');
    iframe.src = "${url}";
    iframe.style.cssText = 'width:100%;height:100%;border:none;border-radius:8px;';
    iframe.title = 'Muslim Resource Guide — The Haal Project';
    iframe.loading = 'lazy';
    el.appendChild(iframe);
  })();
</script>`;
}

export default function MRGEmbedCollection() {
  const [config, setConfig] = useState<EmbedConfig>(DEFAULT_CONFIG);
  const [copied, setCopied] = useState<string | null>(null);
  const [embedType, setEmbedType] = useState<'iframe' | 'script' | 'link'>('iframe');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showSubmitForm, setShowSubmitForm] = useState(false);

  const city = CITIES.find(c => c.id === config.city) ?? CITIES[0];
  const iframeCode = buildIframeCode(config);
  const scriptCode = buildScriptCode(config);
  const directLink = buildEmbedUrl(config);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const accentColor = '#c9a84c';

  return (
    <div style={{ background: '#060d12', minHeight: '100vh', color: '#d4c5a9', fontFamily: 'monospace' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Amiri:ital@0;1&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: rgba(201,168,76,0.2); border-radius: 2px; }
      `}</style>

      {/* Header */}
      <div style={{ padding: '32px 24px 24px', borderBottom: '1px solid rgba(201,168,76,0.1)', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.4)', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 8 }}>
          The Haal Project · Muslim Resource Guide
        </div>
        <h1 style={{ fontSize: 'clamp(20px,4vw,32px)', color: '#F5F0E8', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>
          Embed & Share
        </h1>
        <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.5)', lineHeight: 1.7, maxWidth: 600 }}>
          Plant the Muslim Resource Guide anywhere — in a My Garden plot, on a mosque website, a community org page, or any external site. Configure for your specific city and ward, then copy the embed code.
        </p>
        <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 4, fontSize: 11, color: 'rgba(45,122,71,0.8)', lineHeight: 1.6 }}>
          ◈ Each embed automatically links back to haalproject.org and adds itself to the growing collection of MRG embeds accessible to all caretakers.
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

          {/* ── LEFT: Configuration ── */}
          <div>
            <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 16 }}>
              Configure Your Embed
            </div>

            {/* City */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 9, color: 'rgba(212,197,169,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                City
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {CITIES.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setConfig(prev => ({ ...prev, city: c.id, ward: null }))}
                    style={{
                      padding: '5px 10px',
                      borderRadius: 4,
                      border: `1px solid ${config.city === c.id ? accentColor : 'rgba(255,255,255,0.1)'}`,
                      background: config.city === c.id ? `${accentColor}15` : 'transparent',
                      color: config.city === c.id ? accentColor : 'rgba(212,197,169,0.5)',
                      fontSize: 10,
                      cursor: 'pointer',
                    }}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Ward */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 9, color: 'rgba(212,197,169,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                Ward / Neighborhood (optional — locks embed to specific ward)
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <button
                  onClick={() => setConfig(prev => ({ ...prev, ward: null }))}
                  style={{
                    padding: '5px 10px',
                    borderRadius: 4,
                    border: `1px solid ${config.ward === null ? accentColor : 'rgba(255,255,255,0.1)'}`,
                    background: config.ward === null ? `${accentColor}15` : 'transparent',
                    color: config.ward === null ? accentColor : 'rgba(212,197,169,0.5)',
                    fontSize: 10,
                    cursor: 'pointer',
                  }}
                >
                  All Wards
                </button>
                {city.wards.map(w => (
                  <button
                    key={w.id}
                    onClick={() => setConfig(prev => ({ ...prev, ward: w.id }))}
                    style={{
                      padding: '5px 10px',
                      borderRadius: 4,
                      border: `1px solid ${config.ward === w.id ? accentColor : 'rgba(255,255,255,0.1)'}`,
                      background: config.ward === w.id ? `${accentColor}15` : 'transparent',
                      color: config.ward === w.id ? accentColor : 'rgba(212,197,169,0.5)',
                      fontSize: 10,
                      cursor: 'pointer',
                    }}
                  >
                    {w.shortName}
                  </button>
                ))}
              </div>
            </div>

            {/* Theme */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 9, color: 'rgba(212,197,169,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                Theme
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                {(['dark', 'light', 'forest'] as const).map(t => (
                  <button
                    key={t}
                    onClick={() => setConfig(prev => ({ ...prev, theme: t }))}
                    style={{
                      padding: '5px 12px',
                      borderRadius: 4,
                      border: `1px solid ${config.theme === t ? accentColor : 'rgba(255,255,255,0.1)'}`,
                      background: config.theme === t ? `${accentColor}15` : 'transparent',
                      color: config.theme === t ? accentColor : 'rgba(212,197,169,0.5)',
                      fontSize: 10,
                      cursor: 'pointer',
                      textTransform: 'capitalize',
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Default tab */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 9, color: 'rgba(212,197,169,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                Default Tab
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                {(['resources', 'disparities', 'context'] as const).map(t => (
                  <button
                    key={t}
                    onClick={() => setConfig(prev => ({ ...prev, tab: t }))}
                    style={{
                      padding: '5px 12px',
                      borderRadius: 4,
                      border: `1px solid ${config.tab === t ? accentColor : 'rgba(255,255,255,0.1)'}`,
                      background: config.tab === t ? `${accentColor}15` : 'transparent',
                      color: config.tab === t ? accentColor : 'rgba(212,197,169,0.5)',
                      fontSize: 10,
                      cursor: 'pointer',
                      textTransform: 'capitalize',
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Compact */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={config.compact}
                  onChange={e => setConfig(prev => ({ ...prev, compact: e.target.checked }))}
                  style={{ accentColor }}
                />
                <span style={{ fontSize: 10, color: 'rgba(212,197,169,0.6)' }}>Compact mode (smaller header)</span>
              </label>
            </div>

            {/* Dimensions */}
            <div style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: 9, color: 'rgba(212,197,169,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Width</label>
                <input
                  type="text"
                  value={config.width}
                  onChange={e => setConfig(prev => ({ ...prev, width: e.target.value }))}
                  style={{ width: '100%', padding: '6px 10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#d4c5a9', fontSize: 11, fontFamily: 'monospace' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 9, color: 'rgba(212,197,169,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Height</label>
                <input
                  type="text"
                  value={config.height}
                  onChange={e => setConfig(prev => ({ ...prev, height: e.target.value }))}
                  style={{ width: '100%', padding: '6px 10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#d4c5a9', fontSize: 11, fontFamily: 'monospace' }}
                />
              </div>
            </div>

            {/* Preview button */}
            <button
              onClick={() => setPreviewOpen(v => !v)}
              style={{
                width: '100%',
                padding: '10px',
                background: 'rgba(201,168,76,0.1)',
                border: '1px solid rgba(201,168,76,0.3)',
                borderRadius: 4,
                color: accentColor,
                fontSize: 10,
                letterSpacing: '.12em',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              {previewOpen ? 'Hide Preview' : 'Preview Embed'}
            </button>

            {previewOpen && (
              <div style={{ marginTop: 16, borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(201,168,76,0.2)' }}>
                <iframe
                  src={`/mrg-embed?city=${config.city}${config.ward ? `&ward=${config.ward}` : ''}&theme=${config.theme}${config.compact ? '&compact=true' : ''}&tab=${config.tab}`}
                  style={{ width: '100%', height: 400, border: 'none', display: 'block' }}
                  title="MRG Embed Preview"
                />
              </div>
            )}
          </div>

          {/* ── RIGHT: Embed Code ── */}
          <div>
            <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 16 }}>
              Embed Code
            </div>

            {/* Embed type tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {(['iframe', 'script', 'link'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setEmbedType(t)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 4,
                    border: `1px solid ${embedType === t ? accentColor : 'rgba(255,255,255,0.1)'}`,
                    background: embedType === t ? `${accentColor}15` : 'transparent',
                    color: embedType === t ? accentColor : 'rgba(212,197,169,0.5)',
                    fontSize: 10,
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '.08em',
                  }}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* Code block */}
            <div style={{ position: 'relative' }}>
              <pre style={{
                background: 'rgba(0,0,0,0.4)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 6,
                padding: '16px',
                fontSize: 10,
                color: 'rgba(212,197,169,0.8)',
                overflowX: 'auto',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                minHeight: 120,
              }}>
                {embedType === 'iframe' ? iframeCode : embedType === 'script' ? scriptCode : directLink}
              </pre>
              <button
                onClick={() => copyToClipboard(
                  embedType === 'iframe' ? iframeCode : embedType === 'script' ? scriptCode : directLink,
                  embedType
                )}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  padding: '4px 10px',
                  background: copied === embedType ? 'rgba(45,122,71,0.3)' : 'rgba(201,168,76,0.1)',
                  border: `1px solid ${copied === embedType ? 'rgba(45,122,71,0.5)' : 'rgba(201,168,76,0.3)'}`,
                  borderRadius: 3,
                  color: copied === embedType ? '#2d7a47' : accentColor,
                  fontSize: 9,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                }}
              >
                {copied === embedType ? '✓ Copied' : 'Copy'}
              </button>
            </div>

            {/* Instructions */}
            <div style={{ marginTop: 16, padding: '12px 14px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 4 }}>
              <div style={{ fontSize: 9, color: 'rgba(45,122,71,0.7)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 6 }}>
                How to Use
              </div>
              {embedType === 'iframe' && (
                <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.7 }}>
                  Paste the iframe code into any HTML page, WordPress site, Squarespace, or web builder. Works anywhere that allows HTML embeds.
                </div>
              )}
              {embedType === 'script' && (
                <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.7 }}>
                  Paste the script tag into your page's HTML. The widget will auto-load inside the div. Good for dynamic sites.
                </div>
              )}
              {embedType === 'link' && (
                <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.7 }}>
                  Direct link to the standalone MRG widget. Share in newsletters, social media, or use as a link in your site.
                </div>
              )}
            </div>

            {/* My Garden plant button */}
            <div style={{ marginTop: 16, padding: '14px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 6 }}>
              <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.6)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 6 }}>
                🌱 Plant in My Garden
              </div>
              <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.55)', lineHeight: 1.6, marginBottom: 10 }}>
                Add this MRG embed directly to your Caretaker Garden as a transplantable module.
              </div>
              <a
                href={`/user-dashboard?plant=mrg&city=${config.city}${config.ward ? `&ward=${config.ward}` : ''}`}
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  background: 'rgba(201,168,76,0.1)',
                  border: '1px solid rgba(201,168,76,0.3)',
                  borderRadius: 4,
                  color: accentColor,
                  fontSize: 9,
                  letterSpacing: '.12em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                }}
              >
                Plant in My Garden →
              </a>
            </div>

            {/* Collection note */}
            <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(138,138,184,0.06)', border: '1px solid rgba(138,138,184,0.15)', borderRadius: 4 }}>
              <div style={{ fontSize: 10, color: 'rgba(138,138,184,0.7)', lineHeight: 1.6 }}>
                ◈ Every embed instance contributes to the growing collection of MRG embeds accessible to all caretakers across the platform. Your embed for {city.name}{config.ward ? ` · ${config.ward}` : ''} becomes part of the community's distributed resource network.
              </div>
            </div>

            {/* ── CARETAKER SUBMISSION ── */}
            <div style={{ marginTop: 20, borderTop: '1px solid rgba(45,122,71,0.15)', paddingTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 9, color: 'rgba(45,122,71,0.6)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 4 }}>
                    ⊕ Caretaker Submission
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(212,197,169,0.7)', lineHeight: 1.5 }}>
                    Submit verified resources, disparities, or context for {city.name}{config.ward ? ` · ${config.ward}` : ''} to the MRG collection.
                  </div>
                </div>
                <button
                  onClick={() => setShowSubmitForm(v => !v)}
                  style={{ padding: '8px 16px', background: showSubmitForm ? 'rgba(45,122,71,0.15)' : 'rgba(255,255,255,0.03)', border: `1px solid ${showSubmitForm ? 'rgba(45,122,71,0.5)' : 'rgba(255,255,255,0.1)'}`, borderRadius: 6, color: showSubmitForm ? '#2d7a47' : 'rgba(212,197,169,0.5)', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', flexShrink: 0, transition: 'all 0.2s' }}
                >
                  {showSubmitForm ? '▲ Close' : '⊕ Submit'}
                </button>
              </div>
              {showSubmitForm && (
                <CaretakerSubmissionForm
                  city={config.city}
                  ward={config.ward || ''}
                  accentColor="#2d7a47"
                  onClose={() => setShowSubmitForm(false)}
                  onSubmitted={() => setShowSubmitForm(false)}
                />
              )}
            </div>
          </div>
        </div>

        {/* ── EMBED COLLECTION GALLERY ── */}
        <div style={{ marginTop: 48, borderTop: '1px solid rgba(201,168,76,0.1)', paddingTop: 32 }}>
          <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
            Growing Collection · MRG Embeds
          </div>
          <h2 style={{ fontSize: 20, color: '#F5F0E8', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 16 }}>
            Available City Embeds
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {CITIES.map(c => (
              <div
                key={c.id}
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 6,
                  padding: '14px',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                }}
                onClick={() => setConfig(prev => ({ ...prev, city: c.id, ward: null }))}
              >
                <div style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: 'rgba(201,168,76,0.6)', marginBottom: 4 }}>
                  {c.arabicName}
                </div>
                <div style={{ fontSize: 12, color: '#F5F0E8', fontWeight: 700, marginBottom: 4 }}>{c.name}</div>
                <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.4)', marginBottom: 8 }}>
                  {c.wards.length} wards · {c.wards.reduce((acc, w) => acc + w.resources.length, 0)} resources
                </div>
                <div style={{ fontSize: 9, color: accentColor, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                  Configure Embed →
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
